import { useMutation } from '@tanstack/react-query';
import { type ChangeEvent, useState } from 'react';
import { surveysApi } from '@/api/surveys';
import { Danger, InfoCircle } from '@/components/icons';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { extractApiErrorMessage } from '../builderUtils';
import type { CustomSurveySnapshot, GoogleFormImportResult } from '@/api/surveys';

interface Props {
    hasContent: boolean;
    onConfirm: (snapshot: CustomSurveySnapshot) => void;
    onClose: () => void;
}

const FALLBACK_ERROR =
    'Não foi possível importar o formulário. Verifique o link e tente novamente.';

const countQuestions = (snapshot: CustomSurveySnapshot) =>
    snapshot.sections.reduce((total, section) => total + section.questions.length, 0);

export default function ImportGoogleFormsDialog({ hasContent, onConfirm, onClose }: Props) {
    const [url, setUrl] = useState('');
    const [result, setResult] = useState<GoogleFormImportResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    const importMutation = useMutation({
        mutationFn: () => surveysApi.importGoogleForm(url.trim()),
        onSuccess: (data) => {
            setError(null);
            setResult(data);
        },
        onError: (mutationError) => {
            setResult(null);
            setError(extractApiErrorMessage(mutationError, FALLBACK_ERROR));
        },
    });

    const questionCount = result ? countQuestions(result.snapshot) : 0;

    if (result) {
        return (
            <div className="space-y-4">
                <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">{result.snapshot.name}</p>
                    <p className="text-sm text-muted-foreground">
                        {result.snapshot.sections.length}{' '}
                        {result.snapshot.sections.length === 1 ? 'seção' : 'seções'} ·{' '}
                        {questionCount} {questionCount === 1 ? 'pergunta' : 'perguntas'}
                    </p>
                </div>

                {result.hasBranching ? (
                    <Alert variant="warning" icon={<Danger className="size-4" />}>
                        Este formulário usa &ldquo;ir para a seção com base na resposta&rdquo;. O
                        LaudoPsi não tem ramificação condicional, então todas as perguntas serão
                        feitas a todos os pacientes.
                    </Alert>
                ) : null}

                {result.skipped.length > 0 ? (
                    <Alert variant="warning">
                        <p className="font-medium">
                            {result.skipped.length}{' '}
                            {result.skipped.length === 1
                                ? 'item não pôde ser importado'
                                : 'itens não puderam ser importados'}
                        </p>
                        <ul className="mt-2 space-y-1">
                            {result.skipped.map((skipped, index) => (
                                <li key={`${skipped.title}-${index}`}>
                                    {skipped.title || 'Sem título'} — {skipped.typeLabel}
                                </li>
                            ))}
                        </ul>
                    </Alert>
                ) : null}

                {hasContent ? (
                    <Alert variant="info" icon={<InfoCircle className="size-4" />}>
                        A importação substitui o conteúdo atual do editor, incluindo o nome do
                        formulário.
                    </Alert>
                ) : null}

                {questionCount === 0 ? (
                    <Alert variant="destructive">
                        Nenhuma pergunta compatível foi encontrada neste formulário.
                    </Alert>
                ) : null}

                <div className="flex justify-end gap-2">
                    <Button type="button" variant="surface" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button
                        type="button"
                        disabled={questionCount === 0}
                        onClick={() => onConfirm(result.snapshot)}
                    >
                        {hasContent ? 'Substituir e importar' : 'Importar'}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <form
            className="space-y-4"
            onSubmit={(event) => {
                event.preventDefault();
                if (url.trim()) importMutation.mutate();
            }}
        >
            <div className="space-y-2">
                <Label htmlFor="google-forms-url">Link do formulário</Label>
                <Input
                    id="google-forms-url"
                    value={url}
                    autoFocus
                    placeholder="https://docs.google.com/forms/..."
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setUrl(event.target.value)}
                    disabled={importMutation.isPending}
                />
                <p className="text-xs text-muted-foreground">
                    O formulário precisa aceitar respostas de qualquer pessoa com o link. Nada é
                    enviado ao Google: apenas lemos a estrutura das perguntas.
                </p>
            </div>

            {error ? <Alert variant="destructive">{error}</Alert> : null}

            <div className="flex justify-end gap-2">
                <Button type="button" variant="surface" onClick={onClose}>
                    Cancelar
                </Button>
                <Button type="submit" disabled={!url.trim() || importMutation.isPending}>
                    {importMutation.isPending ? 'Lendo o formulário…' : 'Importar'}
                </Button>
            </div>
        </form>
    );
}
